import React, { useState, useEffect, useCallback } from 'react';
import { 
    XMarkIcon, 
    MagnifyingGlassIcon, 
    UserPlusIcon, 
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';

const CourseEnrollmentModal = ({ courseId, courseName, gradeId, gradeName, onClose, onRefresh }) => {
    const { get, post } = useApi();
    const { showToast } = useAppState();
    const [searchTerm, setSearchTerm] = useState('');
    const [availableStudents, setAvailableStudents] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleSearch = useCallback(async () => {
        setLoading(true);
        try {
            const query = `name=${searchTerm}&grade_level=${gradeId || ''}&learning_area_id=${courseId}`;
            const response = await get(`/teachers/api/students/search/?${query}`);
            setAvailableStudents(response || []);
        } catch (err) {
            showToast('Failed to search registry', 'error');
        } finally {
            setLoading(false);
        }
    }, [get, searchTerm, gradeId, courseId, showToast]);

    // Initial search and debounce
    useEffect(() => {
        const timeout = setTimeout(() => {
            handleSearch();
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchTerm, handleSearch]);

    const handleEnroll = async (studentId) => {
        try {
            await post(`/teachers/api/courses/${courseId}/enroll-students/`, { student_ids: [studentId] });
            showToast('Scholar enrolled successfully', 'success');
            setAvailableStudents(prev => prev.filter(s => s.id !== studentId));
            onRefresh && onRefresh();
        } catch (err) {
            showToast('Enrollment protocol failed', 'error');
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
                <div 
                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" 
                    onClick={onClose}
                ></div>
                
                <div className="relative bg-white w-full max-w-4xl rounded-[2.5rem] shadow-2xl animate-in zoom-in duration-300 flex flex-col my-8">
                    {/* Header */}
                    <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-[2.5rem]">
                        <div>
                            <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight italic leading-none">Enroll Scholars</h2>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-2 italic">
                                {courseName} {gradeName ? <><span className="mx-2 text-slate-300">•</span> {gradeName}</> : ''}
                            </p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-rose-500 hover:border-rose-100 transition-all shadow-sm"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                    <div className="p-8">
                    <div className="space-y-6">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative flex-1 group">
                                <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D]" />
                                <input 
                                    type="text"
                                    placeholder="Search by name or admission ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold text-[#18216D] focus:ring-4 focus:ring-indigo-500/5 transition-all outline-none placeholder:text-slate-300 shadow-inner"
                                />
                            </div>
                            {!courseId && (
                                <div className="px-6 py-4 bg-slate-100/50 rounded-2xl flex items-center text-[10px] font-black text-slate-400 uppercase tracking-widest border border-slate-100">
                                    Filtering: {gradeName || 'Current Grade'}
                                </div>
                            )}
                        </div>

                        <div className="space-y-3">
                            {loading ? (
                                <div className="py-20 text-center flex flex-col items-center">
                                    <div className="w-10 h-10 border-4 border-[#18216D]/10 border-t-[#18216D] rounded-full animate-spin mb-4"></div>
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest animate-pulse">Scanning Grade Registry...</p>
                                </div>
                            ) : availableStudents.length > 0 ? (
                                <>
                                    <div className="grid grid-cols-3 gap-4 px-5 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 mb-2">
                                        <div>Name</div>
                                        <div className="text-center">Admission No.</div>
                                        <div className="text-right">Action</div>
                                    </div>
                                    {availableStudents.map(student => (
                                    <div key={student.id} className="grid grid-cols-3 gap-4 items-center p-5 bg-slate-50 hover:bg-white hover:shadow-xl hover:shadow-indigo-900/5 rounded-2xl border border-transparent hover:border-indigo-50 transition-all group">
                                        <div className="flex items-center">
                                            <p className="text-sm font-black text-[#18216D] italic tracking-tight truncate">{student.name}</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">#{student.student_id}</p>
                                        </div>
                                        <div className="flex justify-end">
                                        {student.is_enrolled ? (
                                            <div className="px-6 py-2.5 bg-emerald-50 text-emerald-600 text-[10px] font-black rounded-xl border border-emerald-100 flex items-center gap-2 uppercase tracking-widest">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Already Enrolled
                                            </div>
                                        ) : (
                                            <button 
                                                onClick={() => handleEnroll(student.id)}
                                                className="px-6 py-2.5 bg-[#18216D] text-white text-[10px] font-black rounded-xl hover:bg-[#0D164F] transition-all uppercase tracking-widest flex items-center gap-2 active:scale-95 shadow-lg shadow-indigo-900/10"
                                            >
                                                <UserPlusIcon className="w-4 h-4" />
                                                Enroll Scholar
                                            </button>
                                        )}
                                        </div>
                                    </div>
                                    ))}
                                </>
                            ) : searchTerm ? (
                                <div className="py-20 text-center bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-100">
                                    <MagnifyingGlassIcon className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] italic">No compatible scholars found in this grade</p>
                                </div>
                            ) : (
                                <div className="py-20 text-center">
                                    <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-inner">
                                        <UserPlusIcon className="w-10 h-10 text-slate-100" />
                                    </div>
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.3em] italic">Ready for Lookup</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                </div>
            </div>
        </div>
    );
};

export default CourseEnrollmentModal;
